import React, { type ReactElement, useState } from 'react';

import { useCookies } from 'react-cookie';

import { defaultLanguage, namespaces } from '@i18n';

import { AUTHENTICATION_CHANGE_LANGUAGE } from '@framework/constants/insights';
import useTranslation from '@framework/hooks/useTranslation';
import { I18N_NEXT_COOKIE } from '@framework/utils/cookies';
import { transformLanguageCode } from '@framework/utils/languages';

import useLanguages from '@hooks/useLanguages';
import useSendStatelessInsight from '@hooks/useSendStatelessInsight';

import { Select } from 'styled-system';

import styles from './LanguageSwitcher.module.scss';

const LanguageSwitcher = (): ReactElement => {
    const { i18n } = useTranslation();
    const languages = useLanguages();

    const sendStatelessInsight = useSendStatelessInsight();

    const [cookies, setCookies] = useCookies([I18N_NEXT_COOKIE]);
    const cookieLanguage = cookies[I18N_NEXT_COOKIE];

    const [selectedLanguage, setSelectedLanguage] = useState(
        transformLanguageCode(cookieLanguage || defaultLanguage, true)
    );

    const handleSetLanguage = async (language: string) => {
        setSelectedLanguage(language);
        setCookies(I18N_NEXT_COOKIE, transformLanguageCode(language), { path: '/' });
        sendStatelessInsight(AUTHENTICATION_CHANGE_LANGUAGE);
        await i18n?.reloadResources(transformLanguageCode(language), namespaces);
        await i18n?.changeLanguage(transformLanguageCode(language));
    };

    const options = languages.map((language) => ({
        label: language.name,
        key: language.value
    }));

    return (
        <div className={styles.container}>
            <Select
                id="language-switcher"
                options={options}
                value={selectedLanguage}
                onChange={handleSetLanguage}
                dropdownMatchSelectWidth={false}
                size="small"
            />
        </div>
    );
};

export default LanguageSwitcher;
